import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useUser } from "../../contexts/UserContext";
import TrialExpiredScreen from "./TrialExpiredScreen";
import { getDefaultAuthenticatedRoute, getUserRoles, isOrganizationUser } from "../../utils/authRoles";

const normalizeKey = (value) => (typeof value === "string" ? value.trim().toLowerCase() : null);

const ProtectedRoute = ({ children, requiredRoles = [] }) => {
  const authToken = localStorage.getItem("hkeAuthToken");
  const { user, isLoading, trialAccess } = useUser();
  const location = useLocation();

  if (isLoading) {
    return null;
  }

  if (!authToken) {
    return <Navigate to="/login" replace />;
  }

  // Check token expiration
  const tokenExpiryTime = 3600 * 1000;
  const tokenCreationTime = Number(localStorage.getItem("tokenCreationTime") || 0);

  if (tokenCreationTime && Date.now() - tokenCreationTime > tokenExpiryTime) {
    localStorage.removeItem("hkeAuthToken");
    localStorage.removeItem("tokenCreationTime");
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (trialAccess?.accessBlocked) {
    return <TrialExpiredScreen />;
  }

  const userType = normalizeKey(user?.user_type);
  const userRole = normalizeKey(user?.role);
  const normalizedRequiredRoles = requiredRoles.map(normalizeKey).filter(Boolean);
  const normalizedUserRoles = new Set(
    [userType, userRole, ...getUserRoles(user).map(normalizeKey).filter(Boolean)].filter(Boolean)
  );
  const orgScopedUser = isOrganizationUser(user);
  const requestsOrganizationAccess = normalizedRequiredRoles.some((role) =>
    ["org_user", "org_master", "org_admin", "organization_admin", "organization_user"].includes(role)
  );
  const requestsNetworkAccess = normalizedRequiredRoles.some((role) =>
    ["network_manager", "network_admin", "network_viewer", "network_user", "network_member"].includes(role)
  );
  const isAllowedByRequiredRoles =
    normalizedRequiredRoles.length === 0 ||
    (
      (!orgScopedUser || requestsOrganizationAccess || requestsNetworkAccess) &&
      normalizedRequiredRoles.some((role) => normalizedUserRoles.has(role))
    );

  if (!isAllowedByRequiredRoles) {
    const redirectPath = getDefaultAuthenticatedRoute(user);

    if (location.pathname !== redirectPath) {
      if (userType === "hke" && !location.pathname.startsWith("/app")) {
        console.warn(`Redirecting '${user?.role}' to ${redirectPath}`);
      }

      return <Navigate to={redirectPath} replace />;
    }

    return <Navigate to="/login" replace />;
  }

  const redirectPath = getDefaultAuthenticatedRoute(user);
  if (location.pathname === "/app/dashboard" && redirectPath !== "/app/dashboard") {
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
